package com.upcominds.expedite.features.dashboard

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.upcominds.expedite.data.admin.AdminRepository
import com.upcominds.expedite.data.challenges.ChallengesRepository
import com.upcominds.expedite.dto.*
import com.upcominds.expedite.model.Challenge
import com.upcominds.expedite.model.Community
import com.upcominds.expedite.model.Quiz
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime

data class DashboardUiState(
    val loading: Boolean = false,
    val error: String? = null,
    val adminContext: AdminContextDTO? = null,
    val mobileLogs: List<MobileLogAdminDTO> = emptyList(),
    val mobileLogsPage: Int = 0,
    val mobileLogsSize: Int = 50,
    val mobileLogsTotalElements: Long = 0,
    val mobileLogsTotalPages: Int = 0,
    val communities: List<Community> = emptyList(),
    val communityAdministratorCandidates: Map<String, List<CommunityAdministratorCandidateDTO>> = emptyMap(),
    val competitions: List<CommunityCompetitionDTO> = emptyList(),
    val selectedCompetition: CommunityCompetitionDTO? = null,
    val selectedCompetitionLeaderboard: CommunityCompetitionLeaderboardDTO? = null,
    val challenges: Map<String, List<ChallengeDTO>> = emptyMap(), // communityId -> List<ChallengeDTO>
    val quizzes: Map<String, List<Quiz>> = emptyMap(), // challengeId -> List<Quiz>
    val communityNews: Map<String, List<CommunityNewsDTO>> = emptyMap(), // communityId -> List<CommunityNewsDTO>
    val questionnaires: List<QuestionnaireDTO> = emptyList(),
    val selectedQuestionnaire: QuestionnaireDetailDTO? = null,
    val puzzles: List<PuzzleDTO> = emptyList(),
    val selectedPuzzle: PuzzleDTO? = null,
    val sponsors: List<SponsorDTO> = emptyList(),
    val rewards: List<RewardDTO> = emptyList(),
    val validatedRedemption: RewardRedemptionDTO? = null
)

class DashboardViewModel(
    private val adminRepository: AdminRepository,
    private val challengesRepository: ChallengesRepository
) : ViewModel() {

    private val _uiState = MutableStateFlow(DashboardUiState())
    val uiState: StateFlow<DashboardUiState> = _uiState.asStateFlow()

    init {
        loadAdminContext()
    }

    fun loadAdminContext() {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val context = adminRepository.getAdminContext()
                _uiState.value = _uiState.value.copy(adminContext = context)
                loadCommunities()
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun clearError() {
        _uiState.value = _uiState.value.copy(error = null)
    }

    fun loadMobileLogs(page: Int = _uiState.value.mobileLogsPage, size: Int = _uiState.value.mobileLogsSize) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val response = adminRepository.getMobileLogs(page, size)
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    mobileLogs = response.content,
                    mobileLogsPage = response.page,
                    mobileLogsSize = response.size,
                    mobileLogsTotalElements = response.totalElements,
                    mobileLogsTotalPages = response.totalPages
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun loadCommunities() {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val communities = adminRepository.getAllCommunities()
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    communities = communities
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun loadCommunityAdministratorCandidates(communityId: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val candidates = adminRepository.getCommunityAdministratorCandidates(communityId)
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    communityAdministratorCandidates = _uiState.value.communityAdministratorCandidates +
                        (communityId to candidates)
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun assignCommunityAdministrator(communityId: String, userId: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                adminRepository.assignCommunityAdministrator(communityId, userId)
                val candidates = adminRepository.getCommunityAdministratorCandidates(communityId)
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    communityAdministratorCandidates = _uiState.value.communityAdministratorCandidates +
                        (communityId to candidates)
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun removeCommunityAdministrator(communityId: String, userId: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                adminRepository.removeCommunityAdministrator(communityId, userId)
                val candidates = adminRepository.getCommunityAdministratorCandidates(communityId)
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    communityAdministratorCandidates = _uiState.value.communityAdministratorCandidates +
                        (communityId to candidates)
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun loadCompetitions() {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val competitions = adminRepository.getCompetitions()
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    competitions = competitions
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun loadCompetition(id: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val competition = adminRepository.getCompetition(id)
                replaceCompetition(competition)
                val leaderboard = if (competition.status == "DRAFT") null else {
                    adminRepository.getCompetitionLeaderboard(id)
                }
                _uiState.value = _uiState.value.copy(selectedCompetitionLeaderboard = leaderboard)
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun createCompetition(
        request: CommunityCompetitionCreateRequest,
        communityIds: List<String>
    ) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val created = adminRepository.createCompetition(request)
                val saved = adminRepository.setCompetitionCommunities(created.id, communityIds)
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    competitions = listOf(saved) + _uiState.value.competitions
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun updateCompetition(
        id: String,
        request: CommunityCompetitionCreateRequest,
        communityIds: List<String>
    ) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                adminRepository.updateCompetition(id, request)
                val saved = adminRepository.setCompetitionCommunities(id, communityIds)
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    competitions = _uiState.value.competitions.map {
                        if (it.id == id) saved else it
                    },
                    selectedCompetition = if (_uiState.value.selectedCompetition?.id == id) saved else _uiState.value.selectedCompetition
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun updateCompetitionSchedule(id: String, request: CompetitionScheduleUpdateRequest) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                replaceCompetition(adminRepository.updateCompetitionSchedule(id, request))
                // The same rankings remain valid (there can be no participation yet).
                _uiState.value = _uiState.value.copy(
                    selectedCompetitionLeaderboard = adminRepository.getCompetitionLeaderboard(id)
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun createCompetitionChallenge(competitionId: String, challenge: Challenge) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val updated = adminRepository.createCompetitionChallenge(
                    competitionId,
                    challenge.toCompetitionChallengeRequest()
                )
                replaceCompetition(updated)
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun updateCompetitionChallenge(competitionId: String, challenge: Challenge) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val updated = adminRepository.updateCompetitionChallenge(
                    competitionId,
                    challenge.id,
                    challenge.toCompetitionChallengeRequest()
                )
                replaceCompetition(updated)
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun deleteCompetitionChallenge(competitionId: String, challengeId: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val updated = adminRepository.deleteCompetitionChallenge(competitionId, challengeId)
                replaceCompetition(updated)
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun activateCompetition(id: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                replaceCompetition(adminRepository.activateCompetition(id))
                _uiState.value = _uiState.value.copy(
                    selectedCompetitionLeaderboard = adminRepository.getCompetitionLeaderboard(id)
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun completeCompetition(id: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                replaceCompetition(adminRepository.completeCompetition(id))
                _uiState.value = _uiState.value.copy(
                    selectedCompetitionLeaderboard = adminRepository.getCompetitionLeaderboard(id)
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun endCompetitionEarly(id: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                replaceCompetition(adminRepository.endCompetitionEarly(id))
                _uiState.value = _uiState.value.copy(
                    selectedCompetitionLeaderboard = adminRepository.getCompetitionLeaderboard(id)
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun deleteCompetition(id: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                adminRepository.deleteCompetition(id)
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    competitions = _uiState.value.competitions.filter { it.id != id },
                    selectedCompetition = null
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    private fun replaceCompetition(competition: CommunityCompetitionDTO) {
        val current = _uiState.value.competitions
        val updatedList = if (current.any { it.id == competition.id }) {
            current.map { if (it.id == competition.id) competition else it }
        } else {
            listOf(competition) + current
        }
        _uiState.value = _uiState.value.copy(
            loading = false,
            competitions = updatedList,
            selectedCompetition = competition
        )
    }

    private fun Challenge.toCompetitionChallengeRequest(): ChallengeCreateRequest {
        val localDeadline = runCatching { LocalDateTime.parse(deadline) }
            .getOrElse { LocalDateTime(LocalDate.parse(deadline), LocalTime(0, 0)) }
        val deadlineDateTime = localDeadline
            .toInstant(TimeZone.currentSystemDefault())
            .toLocalDateTime(TimeZone.UTC)
        return ChallengeCreateRequest(
            name = name,
            description = description,
            imageUrl = imageUrl,
            imageFile = null,
            points = points,
            duration = durationMinutes,
            deadline = deadlineDateTime,
            difficulty = difficulty,
            type = type,
            contentId = contentId,
            savingsConfig = savingsConfig?.let {
                SavingsChallengeConfigCreateRequest(targetReductionPercent = it.targetReductionPercent)
            },
            socialConfig = socialConfig?.let {
                SocialChallengeConfigCreateRequest(
                    platform = it.platform,
                    requiredTag = it.requiredTag,
                    minLikes = it.minLikes,
                    minReposts = it.minReposts
                )
            }
        )
    }

    fun loadChallenges(communityId: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val challenges = adminRepository.getCommunityChallenges(communityId)

                val currentMap = _uiState.value.challenges.toMutableMap()
                currentMap[communityId] = challenges

                _uiState.value = _uiState.value.copy(
                    loading = false,
                    challenges = currentMap
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun loadQuizzes(challengeId: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val quizzes = challengesRepository.getQuizzesByChallenge(challengeId)

                val currentMap = _uiState.value.quizzes.toMutableMap()
                currentMap[challengeId] = quizzes

                _uiState.value = _uiState.value.copy(
                    loading = false,
                    quizzes = currentMap
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun loadQuestionnaires() {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val questionnaires = adminRepository.getAllQuestionnaires()
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    questionnaires = questionnaires
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun loadQuestionnaire(id: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val detail = adminRepository.getQuestionnaire(id)
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    selectedQuestionnaire = detail
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun addQuestionToQuestionnaire(questionnaireId: String, text: String, options: List<String>) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val nextPosition = _uiState.value.selectedQuestionnaire?.questions?.size ?: 0
                val opts = options.map { QuestionnaireOptionCreateRequest(it) }
                val req = QuestionnaireQuestionCreateRequest(text = text, position = nextPosition, options = opts)
                val newQuestion = adminRepository.addQuestionnaireQuestion(questionnaireId, req)

                val currentQuestionnaire = _uiState.value.selectedQuestionnaire
                if (currentQuestionnaire != null && currentQuestionnaire.id == questionnaireId) {
                    val updatedQuestionnaire = currentQuestionnaire.copy(
                        questions = (currentQuestionnaire.questions + newQuestion).sortedBy { it.position }
                    )
                    _uiState.value = _uiState.value.copy(loading = false, selectedQuestionnaire = updatedQuestionnaire)
                } else {
                    loadQuestionnaire(questionnaireId) // Fallback
                }
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun deleteQuestionnaire(id: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                adminRepository.deleteQuestionnaire(id)
                val updatedList = _uiState.value.questionnaires.filter { it.id != id }
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    questionnaires = updatedList
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun updateQuestionnaire(id: String, name: String?, description: String?, infoUrl: String?) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val req = QuestionnaireUpdateRequest(name, description, infoUrl)
                val updated = adminRepository.updateQuestionnaire(id, req)
                
                val updatedList = _uiState.value.questionnaires.map {
                    if (it.id == id) updated else it
                }
                
                var selected = _uiState.value.selectedQuestionnaire
                if (selected != null && selected.id == id) {
                    selected = selected.copy(name = updated.name, description = updated.description, infoUrl = updated.infoUrl)
                }
                
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    questionnaires = updatedList,
                    selectedQuestionnaire = selected
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun createCommunity(community: Community) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val request = CommunityCreateRequest(
                    name = community.name,
                    description = community.description,
                    imageUrl = community.imageUrl,
                    area = community.area,
                    latitude = community.latitude,
                    longitude = community.longitude
                )
                val newCommunity = adminRepository.createCommunity(request)
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    communities = _uiState.value.communities + newCommunity
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun updateCommunity(community: Community) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val request = CommunityUpdateRequest(
                    name = community.name,
                    description = community.description,
                    imageUrl = community.imageUrl,
                    area = community.area,
                    latitude = community.latitude,
                    longitude = community.longitude
                )
                val updatedCommunity = adminRepository.updateCommunity(community.id, request)
                val updatedList = _uiState.value.communities.map {
                    if (it.id == updatedCommunity.id) updatedCommunity else it
                }
                _uiState.value = _uiState.value.copy(loading = false, communities = updatedList)
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun deleteCommunity(id: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                adminRepository.deleteCommunity(id)
                val updatedList = _uiState.value.communities.filter { it.id != id }
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    communities = updatedList
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun createChallenge(challenge: Challenge, communityId: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val date = try {
                    LocalDate.parse(challenge.deadline)
                } catch (e: Exception) {
                    LocalDate(2024, 1, 1)
                }
                val deadlineDateTime = LocalDateTime(date, LocalTime(0, 0))

                val request = ChallengeCreateRequest(
                    name = challenge.name,
                    description = challenge.description,
                    imageUrl = challenge.imageUrl,
                    imageFile = null,
                    points = challenge.points,
                    duration = challenge.durationMinutes,
                    deadline = deadlineDateTime,
                    difficulty = challenge.difficulty,
                    type = challenge.type,
                    contentId = challenge.contentId,
                    savingsConfig = challenge.savingsConfig?.let {
                        SavingsChallengeConfigCreateRequest(
                            targetReductionPercent = it.targetReductionPercent
                        )
                    },
                    socialConfig = challenge.socialConfig?.let {
                        SocialChallengeConfigCreateRequest(
                            platform = it.platform,
                            requiredTag = it.requiredTag,
                            minLikes = it.minLikes,
                            minReposts = it.minReposts
                        )
                    }
                )

                val newChallenge = adminRepository.createChallenge(request, communityId)
                val currentChallenges = _uiState.value.challenges[communityId] ?: emptyList()
                val updatedChallengesMap = _uiState.value.challenges.toMutableMap()
                updatedChallengesMap[communityId] = currentChallenges + newChallenge
                _uiState.value = _uiState.value.copy(loading = false, challenges = updatedChallengesMap)

            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun updateChallenge(challenge: Challenge, communityId: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val date = try {
                    LocalDate.parse(challenge.deadline)
                } catch (e: Exception) {
                    LocalDate(2024, 1, 1)
                }
                val deadlineDateTime = LocalDateTime(date, LocalTime(0, 0))

                val request = ChallengeUpdateRequest(
                    name = challenge.name,
                    description = challenge.description,
                    imageUrl = challenge.imageUrl,
                    imageFile = null,
                    points = challenge.points,
                    duration = challenge.durationMinutes,
                    deadline = deadlineDateTime,
                    difficulty = challenge.difficulty,
                    type = challenge.type,
                    contentId = challenge.contentId,
                    savingsConfig = challenge.savingsConfig?.let {
                        SavingsChallengeConfigCreateRequest(
                            targetReductionPercent = it.targetReductionPercent
                        )
                    },
                    socialConfig = challenge.socialConfig?.let {
                        SocialChallengeConfigCreateRequest(
                            platform = it.platform,
                            requiredTag = it.requiredTag,
                            minLikes = it.minLikes,
                            minReposts = it.minReposts
                        )
                    }
                )

                val updatedChallenge = adminRepository.updateChallenge(communityId, challenge.id, request)
                val currentChallenges = _uiState.value.challenges[communityId] ?: emptyList()
                val updatedChallenges = currentChallenges.map { if (it.id == updatedChallenge.id) updatedChallenge else it }
                val updatedChallengesMap = _uiState.value.challenges.toMutableMap()
                updatedChallengesMap[communityId] = updatedChallenges
                _uiState.value = _uiState.value.copy(loading = false, challenges = updatedChallengesMap)

            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun loadRewardsAdmin() {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null, validatedRedemption = null)
            try {
                val sponsors = adminRepository.getSponsors()
                val rewards = adminRepository.getRewards()
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    sponsors = sponsors,
                    rewards = rewards
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun createSponsor(request: SponsorCreateRequest) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                adminRepository.createSponsor(request)
                val sponsors = adminRepository.getSponsors()
                _uiState.value = _uiState.value.copy(loading = false, sponsors = sponsors)
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun updateSponsor(id: String, request: SponsorCreateRequest) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                adminRepository.updateSponsor(id, request)
                val sponsors = adminRepository.getSponsors()
                _uiState.value = _uiState.value.copy(loading = false, sponsors = sponsors)
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun createReward(sponsorId: String, request: RewardCreateRequest) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                adminRepository.createReward(sponsorId, request)
                val rewards = adminRepository.getRewards()
                _uiState.value = _uiState.value.copy(loading = false, rewards = rewards)
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun updateReward(id: String, request: RewardCreateRequest) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                adminRepository.updateReward(id, request)
                val rewards = adminRepository.getRewards()
                _uiState.value = _uiState.value.copy(loading = false, rewards = rewards)
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun markRedemptionUsed(code: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null, validatedRedemption = null)
            try {
                val redemption = adminRepository.markRedemptionUsed(code)
                _uiState.value = _uiState.value.copy(loading = false, validatedRedemption = redemption)
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun deleteChallenge(communityId: String, challengeId: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                adminRepository.deleteChallenge(communityId, challengeId)
                val currentChallenges = _uiState.value.challenges[communityId] ?: emptyList()
                val updatedChallenges = currentChallenges.filter { it.id != challengeId }
                val updatedChallengesMap = _uiState.value.challenges.toMutableMap()
                updatedChallengesMap[communityId] = updatedChallenges
                _uiState.value = _uiState.value.copy(loading = false, challenges = updatedChallengesMap)
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun createQuiz(quiz: Quiz, challengeId: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val newQuiz = adminRepository.createQuiz(quiz)
                val currentQuizzes = _uiState.value.quizzes[challengeId] ?: emptyList()
                val updatedQuizzesMap = _uiState.value.quizzes.toMutableMap()
                updatedQuizzesMap[challengeId] = currentQuizzes + newQuiz
                _uiState.value = _uiState.value.copy(loading = false, quizzes = updatedQuizzesMap)
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun loadCommunityNews(communityId: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val news = adminRepository.getCommunityNews(communityId)
                val updatedMap = _uiState.value.communityNews.toMutableMap()
                updatedMap[communityId] = news
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    communityNews = updatedMap
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun createCommunityNews(
        communityId: String,
        title: String,
        text: String,
        imageUrl: String?,
        surveys: List<NewsSurveyCreateRequest>
    ) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val normalizedSurveys = surveys
                    .map { survey ->
                        NewsSurveyCreateRequest(
                            question = survey.question.trim(),
                            options = survey.options.map { it.trim() }.filter { it.isNotBlank() }
                        )
                    }
                    .filter { it.question.isNotBlank() && it.options.size >= 2 }
                val request = NewsCreateRequest(
                    title = title,
                    text = text,
                    imageUrl = imageUrl,
                    survey = normalizedSurveys.firstOrNull(),
                    surveys = normalizedSurveys
                )
                val created = adminRepository.createCommunityNews(communityId, request)
                val currentNews = _uiState.value.communityNews[communityId] ?: emptyList()
                val updatedMap = _uiState.value.communityNews.toMutableMap()
                updatedMap[communityId] = listOf(created) + currentNews
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    communityNews = updatedMap
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun updateCommunityNews(
        communityId: String,
        newsId: String,
        title: String,
        text: String,
        imageUrl: String?,
        surveys: List<NewsSurveyCreateRequest>
    ) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val normalizedSurveys = surveys
                    .map { survey ->
                        NewsSurveyUpdateRequest(
                            question = survey.question.trim(),
                            options = survey.options.map { it.trim() }.filter { it.isNotBlank() }
                        )
                    }
                    .filter { it.question.isNotBlank() && it.options.size >= 2 }
                val request = NewsUpdateRequest(
                    title = title,
                    text = text,
                    imageUrl = imageUrl,
                    survey = normalizedSurveys.firstOrNull(),
                    surveys = normalizedSurveys
                )
                val updated = adminRepository.updateCommunityNews(communityId, newsId, request)
                val currentNews = _uiState.value.communityNews[communityId] ?: emptyList()
                val updatedNews = currentNews.map { if (it.id == newsId) updated else it }
                val updatedMap = _uiState.value.communityNews.toMutableMap()
                updatedMap[communityId] = updatedNews
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    communityNews = updatedMap
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun deleteCommunityNews(communityId: String, newsId: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                adminRepository.deleteCommunityNews(communityId, newsId)
                val currentNews = _uiState.value.communityNews[communityId] ?: emptyList()
                val updatedMap = _uiState.value.communityNews.toMutableMap()
                updatedMap[communityId] = currentNews.filter { it.id != newsId }
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    communityNews = updatedMap
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun createQuestionnaire(name: String, description: String?, infoUrl: String?) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val request = QuestionnaireCreateRequest(name, description, infoUrl)
                val newQuestionnaire = adminRepository.createQuestionnaire(request)
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    questionnaires = _uiState.value.questionnaires + newQuestionnaire
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun loadPuzzles() {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val puzzles = adminRepository.getAllPuzzles()
                _uiState.value = _uiState.value.copy(loading = false, puzzles = puzzles)
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun loadPuzzle(id: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val puzzle = adminRepository.getPuzzle(id)
                _uiState.value = _uiState.value.copy(loading = false, selectedPuzzle = puzzle)
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun createPuzzle(name: String, description: String?, numberOfQuestions: Int) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val request = PuzzleCreateRequest(name, description, numberOfQuestions)
                val newPuzzle = adminRepository.createPuzzle(request)
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    puzzles = _uiState.value.puzzles + newPuzzle
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun updatePuzzle(id: String, name: String?, description: String?, numberOfQuestions: Int?) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val req = PuzzleUpdateRequest(name, description, numberOfQuestions)
                val updated = adminRepository.updatePuzzle(id, req)
                
                val updatedList = _uiState.value.puzzles.map {
                    if (it.id == id) updated else it
                }
                
                var selected = _uiState.value.selectedPuzzle
                if (selected != null && selected.id == id) {
                    selected = updated
                }
                
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    puzzles = updatedList,
                    selectedPuzzle = selected
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun addPuzzleQuestion(puzzleId: String, request: PuzzleQuestionCreateRequest) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val newQuestion = adminRepository.addPuzzleQuestion(puzzleId, request)
                val puzzle = _uiState.value.selectedPuzzle
                if (puzzle != null && puzzle.id == puzzleId) {
                    val updatedQuestions = (puzzle.questions ?: emptyList()) + newQuestion
                    val updatedPuzzle = puzzle.copy(questions = updatedQuestions)
                    _uiState.value = _uiState.value.copy(loading = false, selectedPuzzle = updatedPuzzle)
                } else {
                    loadPuzzle(puzzleId) // Fallback
                }
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun deletePuzzle(id: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                adminRepository.deletePuzzle(id)
                val updatedList = _uiState.value.puzzles.filter { it.id != id }
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    puzzles = updatedList
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }
}
